import { ValidationError } from "@core/domain/errors/validation.error";
import { Pagination } from "@core/domain/models/pagination";
import { Either } from "@core/domain/types/either";
import { RoomDatasource } from "@entity/data/datasources/room.datasource";
import { inject, injectable } from "inversify";
import { CreateRoom, Room, Rooms } from "../../domain/models/locals/room.model";
import { RoomsSearchFilters } from "../../domain/models/locals/rooms-search-filters";
import { EntityRoomError } from "@entity/domain/errors/entity.error";
import { RoomMapperError } from "@entity/domain/errors/room/room-mapper.error";

@injectable()
export class RoomRepository {
    constructor(
        @inject(RoomDatasource)
        private readonly roomDatasource: RoomDatasource,
    ) { }

    async getAll(): Promise<Either<EntityRoomError, Room[]>> {
        const roomsResult = await this.roomDatasource.fetchAll();

        return roomsResult
            .mapLeft(() => new EntityRoomError("fetchRoomsError"))
            .map((rooms) => rooms);
    }

    async getAllPaginated(
        limit: number,
        offset: number,
    ): Promise<Either<EntityRoomError, Rooms>> {
        const roomsResult = await this.roomDatasource.fetchAllPaginated(
            limit,
            offset,
        );

        return roomsResult.mapLeft(() => new EntityRoomError("fetchPaginatedRoomsError"));
    }

    async getBy(
        pagination: Pagination,
        filters?: RoomsSearchFilters,
    ): Promise<Either<EntityRoomError, Rooms>> {
        return this.roomDatasource.fetchAllBy(pagination, filters);
    }

    async create(
        createRoom: CreateRoom,
    ): Promise<Either<ValidationError | EntityRoomError | RoomMapperError, Room>> {
        return this.roomDatasource.create(createRoom);
    }

    async delete(roomId: number): Promise<Either<EntityRoomError, boolean>> {
        return this.roomDatasource.delete(roomId);
    }
}
